/**
 * PC-v2.2-B — QUÉ INDICADORES ENSEÑA EL PORTAL
 *
 * El catálogo y la herencia (GENERAL → CLIENTE → CENTRO) viven en el servidor (`backend/src/portal/`); aquí sólo se
 * pregunta `GET /api/portal/indicadores` y se nombran los ids que pinta cada pantalla. No es un segundo catálogo: ni
 * nombres, ni valores por defecto, ni esenciales. `backend/test/pc-v22b-consumo.test.ts` comprueba que estos ids son
 * exactamente los del catálogo.
 *
 * Es PRESENTACIÓN, no acceso: los datos siguen llegando por sus rutas con sus permisos y su RLS. Ocultar una cifra
 * no la quita de la respuesta de datos; una exportación futura no puede apoyarse en esto.
 */
import { api } from '../api.ts';
import { useDatos } from './base.tsx';

export const IND = {
  inicio: {
    previstas: 'inicio.ot.previstas',
    realizadas: 'inicio.ot.realizadas',
    pendientes: 'inicio.ot.pendientes',
    conIncidencia: 'inicio.ot.con_incidencia',
    verificadas: 'inicio.ot.verificadas',
    cumplimiento: 'inicio.cumplimiento',
    verificacion: 'inicio.verificacion',
    fueraDePlazo: 'inicio.atencion.fuera_de_plazo',
    tareasNoOk: 'inicio.atencion.tareas_no_ok',
    incidencias: 'inicio.atencion.incidencias',
    noConformidades: 'inicio.atencion.no_conformidades',
  },
  calendario: {
    total: 'calendario.ot.total',
    situaciones: 'calendario.ot.situaciones',
    conIncidencia: 'calendario.ot.con_incidencia',
    fueraDePlazo: 'calendario.ot.fuera_de_plazo',
    tareas: 'calendario.tareas.resumen',
    situacionesGrafico: 'calendario.ot.situaciones_grafico',
  },
  calidad: {
    actividad: 'calidad.actividad.resumen',
    incAbiertas: 'calidad.incidencias.abiertas',
    incEnAnalisis: 'calidad.incidencias.en_analisis',
    incRegistradas: 'calidad.incidencias.registradas',
    incResueltas: 'calidad.incidencias.resueltas',
    incTiempoMedio: 'calidad.incidencias.tiempo_medio',
    ncActivas: 'calidad.nc.activas',
    ncRegistradas: 'calidad.nc.registradas',
    ncCerradas: 'calidad.nc.cerradas',
    ncTiempoMedio: 'calidad.nc.tiempo_medio',
    ncReaperturas: 'calidad.nc.reaperturas',
    ncPorUbicacion: 'calidad.nc.por_ubicacion',
    ncEvolucion: 'calidad.nc.evolucion',
    ncGravedad: 'calidad.nc.gravedad',
    ncPorCentro: 'calidad.nc.por_centro',
    evolCumplimiento: 'calidad.evolucion.cumplimiento',
    evolOt: 'calidad.evolucion.ot',
    evolIncidencias: 'calidad.evolucion.incidencias',
    evolPorCentro: 'calidad.evolucion.por_centro',
  },
} as const;

export interface IndicadoresVisibles {
  /** null mientras no haya respuesta PARA ESTE centro: nunca la del centro anterior. */
  readonly visibles: ReadonlySet<string> | null;
  readonly error: unknown;
  readonly recargar: () => void;
}

/** `idCentro` '' = todos los centros del cliente (nivel CLIENTE); un id = la configuración efectiva de ese centro. */
export function useIndicadoresPortal(idCentro: string): IndicadoresVisibles {
  const d = useDatos(
    () => api.get<{ idCentro: string | null; visibles: string[] }>(`/api/portal/indicadores${idCentro === '' ? '' : `?idCentro=${encodeURIComponent(idCentro)}`}`),
    [idCentro],
  );
  const vigente = d.datos !== null && (d.datos.idCentro ?? '') === idCentro && !d.cargando ? d.datos : null;
  return { visibles: vigente === null ? null : new Set(vigente.visibles), error: d.error, recargar: d.recargar };
}
